import { Site } from '@pubstack/common/src/site'
import { Control, FieldError, FieldPath, FieldValues, get, useFormState } from 'react-hook-form'
import { Input } from '~/components/Input'
import { Tooltip } from '~/components/Tooltip'

/** The value has to survive as a global variable name on the page, so it is bound by what JavaScript accepts. */
export const PBJS_VARIABLE_NAME_PATTERN = /^[A-Za-z_$][A-Za-z0-9_$]*$/

export const PBJS_VARIABLE_HELPER = 'The global Prebid.js variable this media exposes on the page. Pubstack reads its auctions from it.'
export const PBJS_VARIABLE_IS_REQUIRED = 'A Prebid variable is required'
export const PBJS_VARIABLE_IS_INVALID = 'A Prebid variable is a JavaScript variable name: letters, digits, _ and $, never starting with a digit'
export const PBJS_VARIABLE_IS_OWNER_ONLY = 'Only scope owners can change a media’s Prebid variable.'
export const PBJS_VARIABLE_IS_MANAGED_BY_ONE = 'One serves this media’s Prebid build, so Pubstack sets its variable. Nobody can change it.'
export const PBJS_VARIABLE_WILL_CHANGE =
  'Pubstack will read this media’s auctions from the new variable as soon as you save. If the page exposes a different one, data collection stops until both match — so change it here only once the page is ready.'
export const PBJS_VARIABLE_WILL_CHANGE_TITLE = 'This changes where Pubstack collects from'

/** Why the field is shut — `undefined` when it is open. The two reasons shut it for different people. */
export type PbjsVariableLock = 'managed-by-one' | 'owner-only'

export const PBJS_VARIABLE_LOCK_REASON: Record<PbjsVariableLock, string> = {
  'managed-by-one': PBJS_VARIABLE_IS_MANAGED_BY_ONE,
  'owner-only': PBJS_VARIABLE_IS_OWNER_ONLY,
}

/**
 * One runs its own Prebid build under `kleanadsPbjs`, so on a One media the variable is ad management's to
 * set and nobody's to rename — an owner and a staff member are as shut out as a reader. That is why the One
 * lock is read first: telling an owner the field is owners-only would name a rule that is not the one
 * stopping them.
 */
export const pbjsVariableLockOf = ({ isManagedByOne, isOwner }: { isManagedByOne: boolean; isOwner: boolean }): PbjsVariableLock | undefined =>
  isManagedByOne ? 'managed-by-one' : isOwner ? undefined : 'owner-only'

/**
 * Whether an owner has actually re-pointed the variable — the one question behind both the confirmation
 * asked before saving and the value sent. Someone who cannot edit never changes it, whatever the field holds.
 */
export const hasPbjsVariableChanged = (value: string | undefined, currentValue: string, canEdit: boolean): boolean => canEdit && (value?.trim() ?? '') !== currentValue

/**
 * Empty is tolerated only on a media that is already empty (some predate their scope's Prebid variable).
 * Clearing a set one is refused: the backend writes the empty string through, leaving the media unreadable.
 */
export const validatePbjsVariableName =
  (currentValue: string) =>
  (value: string): true | string => {
    if (!value?.trim()) return currentValue === '' ? true : PBJS_VARIABLE_IS_REQUIRED
    return PBJS_VARIABLE_NAME_PATTERN.test(value) ? true : PBJS_VARIABLE_IS_INVALID
  }

/**
 * Include `pbjsVariableName` only once it actually changed: the backend keeps the stored value only for
 * a body that omits the key, so sending the empty string an untouched field holds would write it in.
 */
export const pbjsVariableNamePayload = (value: string | undefined, currentValue: string, canEdit: boolean): Pick<Site, 'pbjsVariableName'> =>
  hasPbjsVariableChanged(value, currentValue, canEdit) ? { pbjsVariableName: value?.trim() ?? '' } : {}

type Props<T extends FieldValues> = {
  control: Control<T>
  name: FieldPath<T>
  /** What the media exposes today. The warning shows only once the field moves away from it. */
  currentValue: string
  /** Composed by the caller through `pbjsVariableLockOf` — the field adds no gating of its own. */
  lock?: PbjsVariableLock
}

/**
 * The Prebid variable a media is read from. Changing it re-points Pubstack's collection at another
 * global — hence the gates and the warning shown before saving.
 */
export const SitePbjsVariableInput = <T extends FieldValues>({ control, name, currentValue, lock }: Props<T>) => {
  const { errors } = useFormState({ control, name })
  const isReadOnly = !!lock
  const error: FieldError | undefined = get(errors, name)

  return (
    <div className={'flex flex-col gap-4'}>
      {/* A disabled field dispatches no mouse events, so the padlock — which is not disabled — is what the
          reader can actually hover to find out why the field is shut. */}
      <Tooltip title={lock ? PBJS_VARIABLE_LOCK_REASON[lock] : ''} disabled={!lock}>
        <Input
          control={control}
          name={name}
          label={'Prebid variable'}
          readOnly={isReadOnly}
          disabled={isReadOnly}
          // The helper must take its own height — its default absolute placement would let the messages below land on top of it.
          isMessageBlock
          helper={isReadOnly ? undefined : PBJS_VARIABLE_HELPER}
          error={error?.message}
          iconRight={isReadOnly ? 'lock' : undefined}
          rules={isReadOnly ? {} : { validate: validatePbjsVariableName(currentValue) }}
        />
      </Tooltip>
      {/* Re-pointing the variable is warned about on save, in the confirmation that asks for the new one typed back. */}
    </div>
  )
}
